import { useCallback, useEffect, useRef, useState } from 'react';
import { getGuildToggleStates, patchGuildConfig, resetGuildConfig } from '@/lib/api-client/guild-config';
import type { GuildConfigModule } from '@/lib/api-client/guild-config';
import { ApiError } from '@/lib/api-client/client';

/**
 * Shared hook for the 11 "dashboard-toggle" modules (reaction-roles,
 * giveaways, leveling, backup, translation, automod, suggestions,
 * starboard, antinuke, security, reminders) — every one of them backed by
 * bridge/config_registry.py's generic DashboardConfig adapter, so every one
 * of them has the exact same shape on the wire: `{ states: { key: bool } }`.
 * A single-toggle module's only key is `_master`; Security/AutoMod also
 * have real per-feature keys.
 *
 * One hook instance = one module's state for the CURRENTLY SELECTED guild.
 * Re-fetches automatically when `guildId` changes (see the effect below) —
 * a page never has to remember to do that itself, which is what keeps
 * guild-switching from leaking one server's values into another's (see
 * DashboardLayout's guild-switch requirements).
 */

type LoadState =
  | { kind: 'loading' }
  | { kind: 'forbidden' }
  | { kind: 'unavailable'; message: string }
  | { kind: 'ready'; states: Record<string, boolean> };

export function useToggleModule(guildId: string | undefined, module: GuildConfigModule) {
  const [state, setState] = useState<LoadState>({ kind: 'loading' });
  const [draft, setDraft] = useState<Record<string, boolean> | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [resetting, setResetting] = useState(false);
  // Guards against a slow response from a PREVIOUS guild/module landing
  // after the user has already navigated away - without this, switching
  // guilds quickly could apply guild A's late-arriving response on top of
  // guild B's screen.
  const requestToken = useRef(0);

  const load = useCallback(() => {
    if (!guildId) return;
    const token = ++requestToken.current;
    setState({ kind: 'loading' });
    setDraft(null);
    setSaveError(null);
    getGuildToggleStates(guildId, module)
      .then((states) => {
        if (requestToken.current !== token) return;
        setState({ kind: 'ready', states });
      })
      .catch((err: unknown) => {
        if (requestToken.current !== token) return;
        if (err instanceof ApiError && err.code === 'FORBIDDEN') {
          setState({ kind: 'forbidden' });
        } else if (err instanceof ApiError && (err.code === 'BOT_UNREACHABLE' || err.code === 'BOT_DEGRADED')) {
          setState({ kind: 'unavailable', message: 'The bot is temporarily unreachable — try again shortly.' });
        } else {
          setState({ kind: 'unavailable', message: err instanceof ApiError ? err.message : 'Something went wrong loading this.' });
        }
      });
  }, [guildId, module]);

  // Re-fetches whenever the guild OR the module changes — this is what
  // guarantees a guild switch never keeps showing the previous guild's
  // values (requirement: "does not retain the previous guild's values").
  useEffect(() => {
    load();
  }, [load]);

  const current: Record<string, boolean> = state.kind === 'ready' ? (draft ?? state.states) : {};
  const dirty = state.kind === 'ready' && draft !== null && JSON.stringify(draft) !== JSON.stringify(state.states);

  function setFeature(key: string, value: boolean) {
    setDraft({ ...current, [key]: value });
  }

  function setAll(value: boolean) {
    if (state.kind !== 'ready') return;
    const next: Record<string, boolean> = {};
    for (const key of Object.keys(current)) next[key] = value;
    setDraft(next);
  }

  async function save() {
    if (!guildId || state.kind !== 'ready' || !draft) return;
    setSaving(true);
    setSaveError(null);
    try {
      // Only the keys that actually changed — never the whole states
      // object — matching the "send only changed values" requirement.
      const snapshot: Record<string, boolean> = draft;
      const changedFeatures: Record<string, boolean> = {};
      for (const key of Object.keys(snapshot)) {
        const value = snapshot[key];
        if (state.states[key] !== value) changedFeatures[key] = value;
      }
      if (Object.keys(changedFeatures).length === 0) {
        setDraft(null);
        return;
      }
      // "_master" is the single-toggle modules' only key — PATCHed via
      // `all`, matching bridge/config_registry.py's contract exactly;
      // every other key goes through the per-feature `states` path.
      const changes: { all?: boolean; states?: Record<string, boolean> } = {};
      if ('_master' in changedFeatures) changes.all = changedFeatures._master;
      const rest = Object.fromEntries(Object.entries(changedFeatures).filter(([k]) => k !== '_master'));
      if (Object.keys(rest).length > 0) changes.states = rest;

      const res = await patchGuildConfig(guildId, module, changes);
      setState({ kind: 'ready', states: res.states ?? {} });
      setDraft(null);
    } catch (err) {
      setSaveError(err instanceof ApiError ? err.message : 'Could not save — please try again.');
    } finally {
      setSaving(false);
    }
  }

  function discard() {
    setDraft(null);
    setSaveError(null);
  }

  async function reset() {
    if (!guildId) return;
    setResetting(true);
    setSaveError(null);
    try {
      const res = await resetGuildConfig(guildId, module);
      setState({ kind: 'ready', states: res.states ?? {} });
      setDraft(null);
    } catch (err) {
      setSaveError(err instanceof ApiError ? err.message : 'Could not reset — please try again.');
    } finally {
      setResetting(false);
    }
  }

  return { state, current, dirty, saving, saveError, resetting, setFeature, setAll, save, discard, reset, reload: load };
}
